// @ts-check

import React, { useState } from "react"
import _ from "lodash"

import { withStyles } from "@material-ui/core/styles"
import Box from "@material-ui/core/Box"
import Typography from "@material-ui/core/Typography"
import List from "@material-ui/core/List"
import ClearAllIcon from "@material-ui/icons/ClearAll"
import FormControl from "@material-ui/core/FormControl"
import FormGroup from "@material-ui/core/FormGroup"
import Checkbox from "@material-ui/core/Checkbox"
import { useTranslation } from "react-i18next"

// Components
import useStyles from "./styles.js"
import Button from "@material-ui/core/Button"

// Local
import StringArrayToParagraph from "./string-array-to-paragraph"
import OutputOneTabItem from "./output-one-tab-item.js"
import { sortBySubCategory, getOutputItemStatus } from "shared/utils.js"
import CONSTS from "shared/consts"
const { UI_RESULT_TYPES } = CONSTS
import { track } from "../utils/tracking"

export default function OutputOneTab({
  criterion,
  analysisOutput = [],
  scrollToSource,
  isShowAllTests,
  isShowCategories
}) {
  const classes = useStyles({})

  const outputCollapsed = analysisOutput.map((item, itemIndex) => ({
    ...item,
    expanded: false,
    isPassOpen: false,
    isFailOpen: false,
    itemIndex // index in original dataset (prior to sorting them later - see below)
  }))
  
  const { t } = useTranslation()
  const [output, setOutput] = useState(outputCollapsed)

  const collapseAll = () => {
    setOutput(outputCollapsed)
  }

  const toggleExpanded = index => {
    const beforeSelection = output.slice(0, index)
    const selection = output[index]
    const afterSelection = output.slice(index + 1)
    const newOutput = [
      ...beforeSelection,
      { ...selection, expanded: !selection.expanded },
      ...afterSelection,
    ]
    setOutput(newOutput)
  }

  const setProperty = (index, property, value) => {
    const beforeSelection = output.slice(0, index)
    const selection = output[index]
    const afterSelection = output.slice(index + 1)
    const newOutput = [
      ...beforeSelection,
      { ...selection, [property]: value },
      ...afterSelection,
    ]
    setOutput(newOutput)
  }

  const subCategoriesObj = {}
  const subCategoriesArr = []
  output.forEach(({ subCategory }) => {
    if (!subCategoriesObj[subCategory]) {
      subCategoriesObj[subCategory] = true
      subCategoriesArr.push(subCategory)
    }
  })
  
  const subCategoriesSorted = subCategoriesArr.sort(sortBySubCategory)

  const [subCategoryState, setSubCategoryState] = useState(subCategoriesObj)

  const handleChange = (event) => {
    setSubCategoryState({ ...subCategoryState, [event.target.name]: event.target.checked })
  }

  const sortOutputItems = (a, b) => {

    const statusA = getOutputItemStatus(a)
    const statusB = getOutputItemStatus(b)

    const whichComesFirst = {
      A: -1, // i.e. a comes earlier
      B: 1, // i.e. a comes later
      same: 0 // neither must be earlier, leave order as is
    }

    if (statusA === statusB) {
      if (a.subCategory && b.subCategory) {
        return sortBySubCategory(a, b, "subCategory")
      } else {
        return whichComesFirst.same
      }
    }

    if (statusA === UI_RESULT_TYPES.FAIL) {
      return whichComesFirst.A
    } else if (statusB === UI_RESULT_TYPES.FAIL) {
      return whichComesFirst.B
    } else if (statusA === UI_RESULT_TYPES.PASS) {
      return whichComesFirst.A
    } else if (statusB === UI_RESULT_TYPES.PASS) {
      return whichComesFirst.B
    } else if (statusA === UI_RESULT_TYPES.INFO) {
      return whichComesFirst.A
    } else { // => statusB === UI_RESULT_TYPES.INFO (by process of elimination, since we checked above that they are not equal)
      return whichComesFirst.B
    }

  }

  const StyledFormGroup = withStyles(theme => ({
    root: {
      flexDirection: "row",
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
  }))(FormGroup)

  const StyledList = withStyles(theme => ({
    root: {
      marginTop: theme.spacing(-2), 
    },
  }))(List)

  const outputShown = output.filter(item => {
    const isNoSubCategory = !item.subCategory
    const isVisibleSubCategory = subCategoryState[item.subCategory]
    return isNoSubCategory || isVisibleSubCategory
  })
  const outputSorted = outputShown.sort(sortOutputItems)
  const isSubCategoriesShown =
    criterion.showSubCategories &&
    subCategoriesSorted.length > 0 &&
    isShowCategories
  return (
    <Typography component="div" role="tabpanel">
      <Box>
        <div className={classes.outputDescription}>
          <StringArrayToParagraph
            stringArray={criterion.description}
            paragraphClassname={classes.outputDescriptionItem}
          />
        </div>
        <div className={classes.collapseAllWrapper}>
          <div>{isSubCategoriesShown && (
            <FormControl component="fieldset">
              <StyledFormGroup>
                {subCategoriesSorted.map(subCategory => (<>
                  <Checkbox checked={subCategoryState[subCategory]}
                    onChange={handleChange}
                    field={{ name: subCategory }}
                    label={subCategory}
                    form={{ dirty: false, touched: [], errors: [] }}
                    value={subCategoryState[subCategory]}
                  />
                </>))}
              </StyledFormGroup>
            </FormControl>
          )}</div>
          <Button
            onClick={collapseAll}
            disabled={_.isEqual(output, outputCollapsed)}
            size="small"
          >
            <ClearAllIcon />
            {t("collapse_all")}
          </Button>
        </div>
        {outputSorted.length === 0 && (<Typography component="div" role="tabpanel">{t("sorry_no_results_match_your_selection")}</Typography>)}
        {outputSorted.length > 0 && <StyledList disablePadding>
          {outputSorted.map((outputItem) => {
            return (
              <OutputOneTabItem
                outputItem={outputItem}
                toggleExpanded={() => toggleExpanded(outputItem.itemIndex)}
                setIsPassOpen={isOpen => {
                  track("View code snippet example", { 
                    testId: outputItem.code, 
                    testResult: getOutputItemStatus(outputItem),
                    snippetType: "Pass"
                  })
                  setProperty(outputItem.itemIndex, "isPassOpen", isOpen)
                }}
                setIsFailOpen={isOpen => {
                  track("View code snippet example", { 
                    testId: outputItem.code, 
                    testResult: getOutputItemStatus(outputItem),
                    snippetType: "Fail"
                  })
                  setProperty(outputItem.itemIndex, "isFailOpen", isOpen)
                }}
                key={outputItem.title}
                scrollToSource={scrollToSource}
                isSubCategoriesShown={isSubCategoriesShown}
              />
            )
          })}
        </StyledList>}
      </Box>
    </Typography>
  )
}
